import { motion } from 'framer-motion'
import { Link, Routes, Route } from 'react-router-dom'
import { Home, Code2, ShieldCheck, ArrowRight, CheckCircle2 } from 'lucide-react'
import { navigationData } from '../utils/navigation'
import RAGWorkflowsPage from './products/RAGWorkflowsPage'
import GovernancePage from './products/GovernancePage'

const productCategories = [
  {
    icon: Home,
    title: 'Graffiti™ Scrambler',
    description: 'The hardware entry point: one SD card, one small unit, no data center required. Flash it, boot it, and it runs Graffiti™ OS under your own roof.',
    href: '/products/home',
    gradient: 'from-purple-500 to-purple-700',
    features: ['Fully private, locally encrypted', 'Plug + Play setup', 'Compliance-minded design'],
    price: 'Starting at $799',
  },
  {
    icon: ShieldCheck,
    title: '23Controls™ Governance',
    description: 'Graffiti™ OS runs under 23Controls™: an accountable identity behind every unit, a compliance rulebook at ingest time, and a human in the loop for anything ambiguous.',
    href: '/products/governance',
    gradient: 'from-synth-400 to-signal-500',
    features: ['Identity Anchor', 'Compliance Rulebook & Audit Trail', 'RAG & Multi-Model Workflows'],
    price: 'Starter $19/mo',
  },
  {
    icon: Code2,
    title: 'Build with Graffiti™',
    description: "Integrating Graffiti™ OS into your own product or organization? Start with how the governance and retrieval layers actually work before you build on top of them.",
    href: '/products/developer',
    gradient: 'from-signal-500 to-synth-600',
    features: ['Governed by 23Controls™', 'RAG & multi-model workflows', 'Documentation for builders'],
    price: 'Talk to our team',
  },
]

function ProductsIndex() {
  return (
    <div className="pt-24 pb-16">
      {/* Hero */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-synth-950/30 to-void-950" />
        <div className="absolute inset-0 grid-overlay opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold text-void-50 mb-6">
              AI Products for <span className="text-gradient">Every Need</span>
            </h1>
            <p className="text-xl text-void-400 max-w-2xl mx-auto">
              From personal use to enterprise scale, we have the right AI solution for you.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {productCategories.map((product, index) => (
              <motion.div
                key={product.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={product.href}
                  className="group block h-full p-8 rounded-3xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${product.gradient} mb-6`}>
                    <product.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h2 className="text-2xl font-display font-bold text-void-50 mb-3 group-hover:text-synth-300 transition-colors">
                    {product.title}
                  </h2>
                  <p className="text-void-400 mb-6">{product.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {product.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-void-300">
                        <CheckCircle2 className="w-4 h-4 text-synth-500" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-void-800/50">
                    <span className="text-synth-400 font-medium">{product.price}</span>
                    <span className="flex items-center gap-2 text-void-400 group-hover:text-synth-400 transition-colors">
                      Learn More <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

function CategoryPage({ category }) {
  const data = navigationData.products.categories.find(c => c.href.includes(category))
  
  if (!data) return <ProductsIndex />
  
  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-4">
              {data.name}
            </h1>
            <p className="text-xl text-void-400 mb-12">Explore our {data.name.toLowerCase()} offerings</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {data.items.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className="block p-6 rounded-2xl bg-void-900/50 border border-void-800/50 hover:border-synth-700/50 transition-all group"
                  >
                    <h3 className="text-lg font-display font-semibold text-void-100 mb-2 group-hover:text-synth-300 transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-void-400 text-sm">{item.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default function ProductsPage() {
  return (
    <Routes>
      <Route index element={<ProductsIndex />} />
      <Route path="home/*" element={<CategoryPage category="home" />} />
      <Route path="governance" element={<GovernancePage />} />
      <Route path="enterprise/rag-workflows" element={<RAGWorkflowsPage />} />
      <Route path="enterprise/*" element={<CategoryPage category="enterprise" />} />
      <Route path="developer/*" element={<CategoryPage category="developer" />} />
    </Routes>
  )
}